/* @flow */
import MediaTypes from "nexia_mobile/framework/media_type";
import type {
  BackstageUrl,
  DateString,
  Device,
  HumanReadableString,
  PercentInteger,
  Icon,
  IconList,
  Link,
  Schema,
  ServerResponse,
  Url,
  XR724WizardImages,
  ZeroTo255,
} from "nexia_mobile/api/types";
import type { Schedule } from "nx-components/Thermostat/Schedule/utils";

/*
# Device Features

Device features are modular mix-ins that describe that a device has or supports
a certain behavior or functionality. The server sends down an array of features
as one of the attributes on a device, and the client uses these features to
decide what to present to the user.

Each feature is represented as a JSON object that satisfies this shape:
*/
type FeatureShape = {
  name: string,
};
/*

The feature may include whatever metadata it needs at the top-level. For
example, a camera's "connection" feature could look like this:

{
  name: "connection",
  protocol: "wifi",
  signal_strength: 55
}

In this case, `protocol` and `signal_strength` are keys that are specific
to the `connection` feature.

## Actions

Many features contain actions, such as "Lock" or "Unlock", "Set Heat Setpoint",
"Set Dimmer Percentage", etc. These actions are represented as `Link`s; that is,
each action describes an HTTP request that the client could make to perform an
action on the device.

A feature with action `Link`s will have them in an object on its `actions` key,
containing `actionName : Link` entries, where `actionName` can be any
string.
*/
// eslint-disable-next-line no-unused-vars
type FeatureWithActions = FeatureShape & {
  actions: {
    [actionName: string]: Link<*, *>,
  },
};
/*

Generally, the `ResponseBody` of an action `Link` is either
`ServerResponse<Device>`, a `ServerResponse<BackstageUrl>`, or
`ServerResponse<null>`.

Action `Link`s with a `ResponseBody` of type `ServerResponse<Device>` will
respond with the device that the action was performed upon. Usually, performing
the action will change the state of the device somehow.

I'll define a few helper types for common action `Link` shapes here:

Many actions do not require any request body.
*/
type EmptyRequestBody = {||};
/*

Almost all actions are written such that the `Link` response can be either a
`ServerResponse<BackstageUrl>` or `ServerResponse<Device>` and the mobile client
will act appropriately depending on what it got.
*/
type BackstageOrDeviceResponse = ServerResponse<BackstageUrl | Device>;
/*

Most actions do not require any request params and respond with a
BackstageOrDeviceResponse.
*/
type SimpleActionLink = Link<EmptyRequestBody, BackstageOrDeviceResponse>;
/*

## Schema Actions

Some actions require a user to fill out a form or select some options in order
to be executed, for example choosing which sprinkler zones to water, or
assigning button actions to a scene controller. In those cases, the action Link
will have this shape:
*/
type SchemaActionLink = Link<EmptyRequestBody, ServerResponse<Schema>> & {
  data?: Schema, // If data is not present, you can fetch the Link to get it.
  type: typeof MediaTypes.SCHEMA,
};
/*

To handle these actions, the client first requests the Schema via the Link (or
uses the Schema in `data` if it's present), then uses the Schema to render a
form or options and submit the action (the Schema will contain a "commit" Link
that submits it).

## Backwards-Compatibility

Features need to be implemented in a backwards-compatible way; that is, a phone
running an older version of the mobile app should not break because we changed
the way one of our device features works. Luckily, this is not difficult to do
if you design features thoughtfully and keep a few things in mind:

When the mobile client parses a device, it looks at the name of each feature the
device has in order to find and run a handler for each feature that augments
that device's functionality. If the client does not know how to handle a
particular feature, it will skip over it. As such:

> It is safe to add additional features without breaking backwards
compatibility.

When each feature handler runs, if there are additional metadata or actions
present in the feature that the handler does not use, they do not hurt anything.
Therefore:

> It is safe to add additional metadata to a feature without breaking backwards
compatibility.

> It is safe to add additional actions to a feature without breaking backwards
compatibility.

Feature handlers can be written to assume certain actions and metadata are
present, or they can be written to check if they are present before using them.
In the latter case, the metadata or action is considered "optional". Because the
feature handlers ship with the client, if ANY mobile client considers part of a
feature to be non-optional, it must always be present. Therefore:

> It *MAY* be safe to remove actions from a feature without breaking backwards
compatibility, but *ONLY* if that action has *ALWAYS* been optional.

 > It *MAY* be safe to remove metadata from a feature without breaking
backwards compatibility, but *ONLY* if that piece of metadata has *ALWAYS* been
optional.

> It is *NEVER* safe to remove non-optional actions or metadata (without
breaking backwards-compatibility).

It can be difficult to know which versions of the mobile app treat feature
components as non-optional and which treat them as optional. Documenting that
information is the main reason this file exists.

## Gotcha: Client-side synthesized features

Features *should* always be sent down from the server. However, on some versions
of the mobile client, features are synthesized client-side at device parse time
to appear as if they had been sent down from the server. This can make things
very confusing and is something we want to get away from and stop doing.

Some features defined in this document have never been sent from the server and
have only been synthesized client-side. Other features are both sent down from
the server *and* are synthesized client-side. There are also a few features that
have never been synthesized client-side and have only ever been sent from the
server.

Client-synthesized features are added to the end of the array of features that
was sent from the server. Therefore, if the server sends down a "thermostat"
feature, and the client also synthesizes a "thermostat" feature, the feature
handler for "thermostat" will be called twice; once for the server's feature,
and then the client's feature. Most handlers are written in a way that calling
them twice with an effectively identical feature will not have an adverse
effect. The danger comes in when the feature the client synthesized is not the
same as what the server sent. In this case, the behavior is undefined and
depends on how the synthesization and feature handler were implemented.

At some point in the future, the client may always consider what the server sent
as higher precedence than what the client is synthesizing, but this has not yet
been implemented.

We want to get to a world where features *only* come from the server, and the
client has no say in it. Features coming from both sources causes a lot of
confusion and unexpected behavior.

## Feature Specifications

Below is a list of all current and past device features and which parts of them
are optional and which are required. As you create new features, you should
document them here.

When you create new features, it is preferred to define as many parts of the
feature as optional as possible, because it leaves more room for flexibility in
the future.

Additionally, some features have been deprecated, but are still sent down from
the server to support older mobile clients. If a feature is deprecated, this
documentation should be updated to reflect that.
---

### access_code
* This feature has been synthesized client-side for as long as it has existed.
* This feature has never been sent down from the server.
* This feature was written to handle temporary codes on locks with keypad entry.
* Mobile clients that synthesize this feature client-side assume the following
  endpoints are valid server-side:
  * `POST <device url>/set_temporary_code`
  * `POST <device url>/remove_temporary_code`

*/
export type AccessCodeFeature = {
  name: "access_code",
  has_temporary: boolean,
  code_length: number,
  actions: {
    set_temporary: Link<{| code: string |}, BackstageOrDeviceResponse>,
    remove_temporary: SimpleActionLink,
  },
  resource?: {
    href: string, // This href is a GET-able list of user codes
  },
};
/*

### action_buttons
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature was written as a generic feature for devices that have actions
  that do not require any request parameters. One or more buttons will be
  presented side-by-side in the tile and details view. Each button can have
  an icon and can have a label.

*/
export type ActionButtonsFeature = {
  name: "action_buttons",
  actions: {
    [name: string]: SimpleActionLink & {
      button_text: HumanReadableString | null,
      disabled?: boolean,
      icon?: IconList,
    },
  },
};
/*

### advanced_info
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is used to show information and buttons on the device advanced info page.

*/
export type AdvancedInfoItem = {
  label?: HumanReadableString,
  value?: HumanReadableString,
  load_link_label?: HumanReadableString,
  timeout_message?: HumanReadableString,
  warning_message?: HumanReadableString,
  alert_result?: boolean,
  load_link?: Link<
    {||},
    ServerResponse<{|
      value?: HumanReadableString,
      error_message?: HumanReadableString,
    |}>
  >,
};
export type AdvancedInfoFeature = {
  name: "advanced_info",
  items: Array<AdvancedInfoItem>,
};
/*

### alarm
* This feature has been synthesized client-side.
* This feature has been sent down from the server.
* This feature is intended to be used with sirens, flashing hazard lights, etc.
* Mobile clients that synthesize this feature client-side assume the following
  endpoints are valid server-side:
  * `POST <device url>/on`
  * `POST <device url>/off`
* TODO: This is (almost) exactly the same as switch

*/
export type AlarmFeature = {
  name: "alarm",
  status: HumanReadableString,
  actions?: {
    toggle?: SimpleActionLink,
  },
};
/*

### barrier
* This feature has been synthesized client-side for as long as it has existed.
* This feature has never been sent down from the server.
* This feature is intended to be used with barriers; garage doors, etc.
* Mobile clients that synthesize this feature client-side assume the following
  endpoints are valid server-side:
  * `POST <device url>/operate`

*/
export type BarrierFeature = {
  name: "barrier",
  status: HumanReadableString,
  actions: {
    operate?: SimpleActionLink & {
      title: HumanReadableString, // what the action will do; "Open", "Close"
    }, // operate action is not present when device is inoperable
  },
};
/*

### battery
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with devices that have a battery.

*/
export type BatteryFeature = {
  name: "battery",
  label?: HumanReadableString, // the battery status
  // * If `label` ends in "remaining", some mobile clients trim that part off
  updated_at: DateString,
  icon?: IconList,
};
/*

### button_pad
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with devices that have a button pad
  (One Touch, other Scene controllers).

*/
export type ButtonPadFeature = {
  name: "button_pad",
  actions: {
    assign: SchemaActionLink, // Assign names and actions to the buttons
  },
};
/*

### camera
* This feature has been synthesized client-side.
* This feature has not been sent down from the server.
* This feature is intended to be used with IP cameras.

*/
export type CameraFeature = {
  name: "camera",
  connected: boolean,
  aspect_ratio?: number, // eg 16 / 9
  /* deprecated */ quality: "low" | "high" | "hd" | "unknown",
  // `quality` has been converted to a setting on the device,
  // so this property is deprecated.
};
/*

### colored
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature can be used with any device that supports "setting color", but
  was written with colored LED bulbs in mind.

*/
type Colors = {
  red: ZeroTo255,
  blue: ZeroTo255,
  green: ZeroTo255,
  warm: ZeroTo255, // "warm light"
  cool: ZeroTo255, // "cool light"
};

export type ColoredFeature = {
  name: "colored",
  colors: Colors,
  actions: {
    set_color: Link<Colors, BackstageOrDeviceResponse>,
  },
};
/*

### connection
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with any device that wants to present
  connection strength information (eg WiFi signal strength).

*/
export type ConnectionFeature = {
  name: "connection",
  protocol?: "wifi", // not currently used
  signal_strength: PercentInteger | "unknown",
  is_connected?: boolean,
};
/*

### dimmer
* This feature has been synthesized client-side.
* This feature has not been sent down from the server.
* This feature is intended to be used with dimmable lights.
* Mobile clients that synthesize this feature client-side assume the following
  endpoints are valid server-side:
  * `POST <device url>/set`

*/
export type DimmerFeature = {
  name: "dimmer",
  level: PercentInteger,
  actions: {
    set_level: Link<{| value: PercentInteger |}, BackstageOrDeviceResponse>,
  },
};
/*

### dragonfly_wifi_configuration
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature indicates that the device is a Dragonfly and should show a
  clientside wizard allowing the user to configure the device's WiFi connection.

*/
export type DragonflyWifiConfigurationFeature = {
  name: "dragonfly_wifi_configuration",
  // This "get_last_update_time" Link may be used by the wizard to discover
  // when the Dragonfly has reconnected (using the new WiFi configuration). If
  // the device was last updated at a time after when the wizard knew it was
  // offline, then the device must have reconnected.
  get_last_update_time: Link<
    {},
    ServerResponse<{| last_update_time: ?DateString |}>
  >,
};
/*

### fan
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with mechanical fans, eg. ceiling fans.

*/
type FanSpeed = "Off" | "Low" | "Medium" | "High";

export type FanFeature = {
  name: "fan",
  status: "off" | "low" | "medium" | "high" | "unknown",
  speed: PercentInteger, // 0 means stopped, 100 means very fast
  speed_increment: number, // increment by which the fan can change speed, eg 33
  actions: {
    set_speed: Link<{| level: FanSpeed |}, BackstageOrDeviceResponse>,
  },
};
/*

### group
* This feature has been synthesized client-side.
* This feature has been sent down from the server (but only for zoned XXLs).
* This feature is intended to be used with devices that have children. (Groups
  in portal are represented as devices with the group feature in mobile).
* This feature is used both for Groups as well as Zoned Thermostats; a Zoned
  Thermostat will have a group feature and a thermostat feature, and the members
  of the group feature will contain one device for each XxlZone.
* If a device has a group feature, it may also have an edit link that provides a
  Schema to edit that group.

*/
export type GroupFeature = {
  name: "group",
  members: Array<Device>,
};
/*

### lock_lock
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used for locks that can be remotely locked

*/
export type LockLockFeature = {
  name: "lock_lock",
  is_locked: boolean,
  actions: {
    lock: SimpleActionLink,
  },
};
/*

### unlock_lock
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used for locks that can be remotely unlocked

*/
export type UnlockLockFeature = {
  name: "unlock_lock",
  is_unlocked: boolean,
  actions: {
    unlock: SimpleActionLink,
  },
};
/*


### activate_lock
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used for locks that can be remotely activated

*/
export type ActivateLockFeature = {
  name: "activate_lock",
  is_activatable: boolean,
  actions: {
    activate: SimpleActionLink,
  },
};
/*

### occupancy
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used for devices that may be occupied and/or
  have an "occupancy mode". Right now that's only Nest Structures.

*/
type OccupancyMode = "home" | "away" | "auto-away" | "unknown";

export type OccupancyFeature = {
  name: "occupancy",
  value: OccupancyMode,
  actions: {
    set_value: Link<{| value: OccupancyMode |}, BackstageOrDeviceResponse>,
  },
};
/*

### PivotFeature
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is for commercial thermostats that will support presets
  named as "none, ""occupied" and "unoccupied".

*/
type PivotOccupancyMode = "none" | "occupied" | "unoccupied";

type OccupancySetpoints = {
  preset_heat1: number,
  preset_cool1: number,
  preset_heat2: number,
  preset_cool2: number,
  setpoint_limit_enabled: boolean,
  setpoint_limit: number,
};

type setHighLowTempAlert = {
  high_low_enabled: boolean,
  high_limit: number,
  low_limit: number,
};

type pinAccess = {
  pin_enabled: boolean,
  pin_passcode: string,
  technican_pin_enabled: boolean,
  technican_passcode: string,
};

export type PivotFeature = {
  name: "pivot",
  device_identifier: string,
  occupancy_setpoints: OccupancySetpoints,
  occupancy_mode: PivotOccupancyMode,
  max_period_name_length: number,
  max_periods_per_day: number,
  high_low_temp_alert: setHighLowTempAlert,
  sensor_fault_enabled: boolean,
  pin_access: pinAccess,
  access_level: string,
  actions: {
    set_occupancy_mode: Link<
      { value: PivotOccupancyMode },
      ServerResponse<null>
    >,
    set_occupancy_setpoints: Link<
      { setpoints: OccupancySetpoints },
      ServerResponse<null>
    >,
    set_group_occupancy_setpoints: Link<
      {
        device_ids: Array<string>,
        setpoints: OccupancySetpoints,
      },
      ServerResponse<null>
    >,
    set_group_schedule: Link<
      {
        schedule: Schedule,
        device_ids: Array<string>,
      },
      ServerResponse<null>
    >,
    get_schedules_with_default: Link<
      {},
      ServerResponse<{ items: Array<Schedule> }>
    >,
    set_high_low_temp_alert: Link<
      {
        temperature_high_limit: number,
        temperature_low_limit: number,
        notified_for_temperature: boolean,
      },
      ServerResponse<null>
    >,
    set_group_high_low_temp_alert: Link<
      {
        device_ids: Array<string>,
        temperature_high_limit: number,
        temperature_low_limit: number,
        notified_for_temperature: boolean,
      },
      ServerResponse<null>
    >,
    set_sensor_fault_enabled: Link<
      {
        value: boolean,
      },
      ServerResponse<null>
    >,
    set_group_sensor_fault_enabled: Link<
      {
        device_ids: Array<string>,
        value: boolean,
      },
      ServerResponse<null>
    >,
    set_pin_access: Link<
      {
        pinAccess: pinAccess,
      },
      ServerResponse<null>
    >,
    set_group_pin_access: Link<
      {
        device_ids: Array<string>,
        pinAccess: pinAccess,
      },
      ServerResponse<null>
    >,
    set_access_level: Link<
      {
        access_level_type: string,
      },
      ServerResponse<null>
    >,
    set_group_access_level: Link<
      {
        device_ids: Array<string>,
        access_level_type: string,
      },
      ServerResponse<null>
    >,
  },
};
/*

### pan_tilt
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with cameras that support pan tilt.

*/
export type PanTiltFeature = {
  name: "pan_tilt",
  actions: {
    up: SimpleActionLink,
    down: SimpleActionLink,
    left: SimpleActionLink,
    right: SimpleActionLink,
  },
};
/*

### runtime_history
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used to show thermostat runtime history.

*/
type RuntimeHistoryData = {
  show_monthly_runtime_history: boolean,
  table: {
    headings: Array<HumanReadableString>,
    series: Array<{|
      name: HumanReadableString,
      data: Array<number | HumanReadableString>,
    |}>,
  },
  chart: Object, // Any valid highcharts config
};

export type RuntimeHistoryFeature = {
  name: "runtime_history",
  actions: {
    get_runtime_history: Link<
      EmptyRequestBody,
      ServerResponse<RuntimeHistoryData>
    >,
    get_monthly_runtime_history: Link<
      EmptyRequestBody,
      ServerResponse<RuntimeHistoryData>
    >,
  },
};
/*

### room_iq
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with xxls that support RoomIQ
*/
export type RoomIqFeature = {
  name: "room_iq",
  href: Url,
};
/*

### schedule
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used to view/edit a thermostat's operating
  schedule.

*/

export type ScheduleFeature = {
  name: "schedule",
  enabled: boolean,
  max_period_name_length: number,
  collection_url: Url,
  can_add_remove_periods: boolean,
  max_periods_per_day: number,
  actions: {
    get_active_schedule?: Link<EmptyRequestBody, ServerResponse<BackstageUrl>>,
    get_default_schedule?: Link<EmptyRequestBody, ServerResponse<Schedule>>,
    set_active_schedule?: Link<Schedule, ServerResponse<BackstageUrl>>,
    enable_scheduling?: SimpleActionLink & ?{ data: { value: boolean } },
  },
};
/*

### sensor
* This feature has been synthesized client-side.
* This feature has been sent down from the server.
* This feature is intended to be used for sensors, including embedded sensors.
  For example a temperature sensor, humidity sensor, light sensor, etc.
* The client assumes that there will only be one sensor feature per sensor type
  per device.

*/
export type SensorFeature = {
  name: "sensor",
  sensor_type: string,
  value: HumanReadableString | number, // eg 72
  unit: HumanReadableString, // eg "°"
  status?: ?HumanReadableString,
};
/*

### solar_panel
* This feature has been synthesized client-side.
* This feature has not been sent down from the server.
* This feature is intended to be used for solar panels, eg enphase.

*/
export type SolarPanelFeature =
  | {
      name: "solar_panel",
      is_setup: false,
    }
  | {
      name: "solar_panel",
      is_setup: true,
      history_chart: Object, // Any valid highcharts config
      energy_production_current: number,
      energy_production_today: number,
      energy_production_month: number,
      energy_production_lifetime: number,
      dollars_saved_today: number,
      dollars_saved_month: number,
      dollars_saved_lifetime: number,
    };
/*

### speaker
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used for multimedia speakers, eg Sonos.

*/
export type SpeakerFeature = {
  name: "speaker",
  status: HumanReadableString,
  volume: number,
  is_playing: boolean,
  favorites: any, // TODO
  actions: {
    play: SimpleActionLink,
    pause: SimpleActionLink,
    set_volume: Link<{| volume: number |}, BackstageOrDeviceResponse>,
    play_url: Link<{| url: Url |}, BackstageOrDeviceResponse>,
    play_favorite: Link<{| title: string |}, BackstageOrDeviceResponse>,
    // The below two actions don't appear to have ever been implemented
    // serverside, but the clientside code supports them:
    seek_previous?: SimpleActionLink,
    seek_next?: SimpleActionLink,
  },
};
/*

### sprinkler
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with sprinkler controllers, eg Rachio.

*/
export type SprinklerFeature = {
  name: "sprinkler",
  actions: {
    standby_mode: Link<
      {| value: "true" | "false" | "1" | "0" |},
      BackstageOrDeviceResponse
    >,
    water_zones: SchemaActionLink,
    stop_water: SimpleActionLink,
  },
};
/*

### streamable
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with nest cameras.

*/
export type StreamableFeature = {
  name: "streamable",
  stream_app_url: Url, // eg nestmobile://something
  stream_web_url: Url, // eg http://nest.com/somewhere
  connected: boolean,
  value: boolean, // Whether the device has streaming enabled
  actions: {
    // Enable/disable streaming
    set_value: Link<{| value: boolean |}, BackstageOrDeviceResponse>,
  },
};
/*

### switch
* This feature has been synthesized client-side.
* This feature has not been sent down from the server.
* This feature is intended to be used for light switches and appliance modules.
* Mobile clients that synthesize this feature client-side assume the following
  endpoints are valid server-side:
  * `POST <device url>/on`
  * `POST <device url>/off`

*/
export type SwitchFeature = {
  name: "switch",
  status: "on" | "off",
  actions: {
    toggle?: SimpleActionLink,
    on?: SimpleActionLink,
    off?: SimpleActionLink,
  },
};
/*

### thermostat
* This feature was synthesized client-side up through 4.10.x.
* This feature is no longer synthesized client-side as of 4.11.0.
* This feature has been sent down from the server.
* This feature is intended to be used with thermostats or thermostat zones.
* Mobile clients that synthesize this feature client-side assume the following
  endpoints are valid server-side:
  * `POST <device url>/setpoints`

*/
export type ThermostatFeature = {
  name: "thermostat",
  temperature: number, // Current indoor temperature
  status: HumanReadableString, // Heating, Cooling, Idle, Not Connected, etc
  status_icon: ?Icon,
  setpoint_delta: number, // Distance the heat and cool setpoint need to be from
  // each other, in degrees. (Eg 3)
  setpoint_increment: 1 | 0.5, // Amount the thermostat changes setpoints by
  setpoint_heat_min: number, // Minimum value the heat setpoint can be
  setpoint_heat_max: number, // Maximum value the heat setpoint can be
  setpoint_cool_min: number, // Minimum value the cool setpoint can be
  setpoint_cool_max: number, // Maximum value the cool setpoint can be
  setpoint_heat?: number, // Current heat setpoint value
  setpoint_cool?: number, // Current cool setpoint value
  alert_temperature_low?: number, // Current lower-bound temp to send alerts at
  alert_temperature_high?: number, // Current upper-bound temp to send alerts at
  actions: {
    set_heat_setpoint?: Link<{| heat: number |}, BackstageOrDeviceResponse>,
    set_cool_setpoint?: Link<{| cool: number |}, BackstageOrDeviceResponse>,
    set_setpoints?: Link<
      | {| heat: number, cool: number |}
      | {| heat: number |}
      | {| cool: number |},
      BackstageOrDeviceResponse
    >,
  },
  system_status?: HumanReadableString, // Only present on zones; the status of
  // the parent XXL
};

/*
### thermostat_compressor_speed
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature contains the current compressor speed of the system.
*/
export type ThermostatCompressorSpeedFeature = {
  name: "thermostat_compressor_speed",
  compressor_speed: number, // Float, 0.0 to 1.0
};

/*
### ThermostatModeShape
Base feature shape to power the buttons on the details page
*/

type ThermosatModeShape = {
  label: string,
  options: Array<{|
    id: string,
    label: string,
    value: string,
    header?: boolean,
  |}>,
  display_value: string,
  value: string,
};

/*
### thermostat_fan_mode
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature powers the Fan Mode button on the device details page
* It can apply to Z-Wave, XXL, and xr724 thermostats
*/
export type ThermostatFanModeFeature = ThermosatModeShape & {
  name: "thermostat_fan_mode",
  status_icon: ?Icon,
  actions: {
    update_thermostat_fan_mode: Link<
      {| value: string |},
      BackstageOrDeviceResponse
    >,
  },
};

/*
### thermostat_mode
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature powers the Fan Mode button on the device details page
* It can apply to Z-Wave, XXL, and xr724 thermostats
*/
export type ThermostatModeFeature = ThermosatModeShape & {
  name: "thermostat_system_mode" | "thermostat_zone_mode",
  actions: {
    update_thermostat_mode: Link<
      {| value: string |},
      BackstageOrDeviceResponse
    >,
  },
};

/*
### thermostat_run_mode
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature powers the Run Mode button on the device details page
* It can apply to Z-Wave, XXL, and xr724 thermostats
*/
export type ThermostatRunModeFeature = ThermosatModeShape & {
  name: "thermostat_run_mode",
  actions: {
    update_thermostat_run_mode: Link<
      {| value: string |},
      BackstageOrDeviceResponse
    >,
  },
};

/*
### pivot_occupancy_mode
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature powers the Occupancy mode override on the thermostat options page
* It can apply to pivot thermostats
*/
export type PivotOccupancyModeFeature = ThermosatModeShape & {
  name: "pivot_occupancy_mode",
  setpoints_override: {
    occupancy_override_mode: number,
  },
  occupancy_override: {
    current_time: DateString,
    occupancy_override_enabled: boolean,
    occupancy_override_start_time: DateString,
    occupancy_override_time: DateString,
  },
  actions: {
    set_occupancy_override: Link<{| value: string |}, ServerResponse<null>>,
  },
};

/*

### unenroll
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used for devices that can be unenrolled. So far
  it's only ever been used with Sonos, which has never been used in production
  (except by George).

*/
export type UnenrollFeature = {
  name: "unenroll",
  actions: {
    // If `unenroll` is a SchemaActionLink, then the device has guided unenrollment.
    // That idea doesn't seem to be fully fleshed out, though, because all we do
    // if a device has guided unenrollment is we don't show a simple "unenroll"
    // button in the details view (because presumably the guided enrollment
    // happens somewhere else).
    unenroll: SchemaActionLink | SimpleActionLink,
  },
};
/*

### valve
* This feature has been synthesized client-side for as long as it has existed.
* This feature has never been sent down from the server.
* This feature is intended to be used with water valves that can open/close.
* Mobile clients that synthesize this feature client-side assume the following
  endpoints are valid server-side:
  * `POST <device url>/operate`
* TODO: Looks like this is exactly the same as the barrier feature

*/
export type ValveFeature = {
  name: "valve",
  status: HumanReadableString,
  actions: {
    operate?: SimpleActionLink & {
      title: HumanReadableString, // what the action will do; "Open", "Close"
    }, // operate action is not present when device is inoperable
  },
};
/*

### deprecated: window_covering
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is used to represent window shades. It has been deprecated in
  favor of `window_shade_open_close_stop` and `window_shade_set_percent_open`,
  but the server still sends this down for backwards compatibility with older
  mobile clients. The current mobile client intentionally doesn't support it,
  because the server will always send one of `window_shade_open_close_stop`
  or `window_shade_set_percent_open` along with the deprecated `window_covering`
  feature.

*/
// not exported because deprecated
// eslint-disable-next-line no-unused-vars
type WindowCoveringFeature = {
  name: "window_covering",
  percent_closed: PercentInteger,
  actions: {
    set_percent_closed: Link<
      {| percent_closed: PercentInteger |},
      BackstageOrDeviceResponse
    >,
  },
};
/*

### window_shade_open_close_stop
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with window shades that can only be
  opened, closed, or stopped (as in, you start opening, then stop it at the
  preferred angle). Window shades that can be set to an arbitrary percentage
  should use the window_shade_set_percent_open feature instead.

*/
export type WindowShadeOpenCloseStopFeature = {
  name: "window_shade_open_close_stop",
  percent_open: PercentInteger,
  actions: {
    open: SimpleActionLink,
    close: SimpleActionLink,
    stop: SimpleActionLink,
  },
};
/*

### window_shade_set_percent_open
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature is intended to be used with window shades that can be set to an
  arbitrary percentage (like how a dimmer works). Window shades that can only be
  opened, closed, or stopped should use the window_shade_open_close_stop feature
  instead.

*/
export type WindowShadeSetPercentOpenFeature = {
  name: "window_shade_set_percent_open",
  percent_open: PercentInteger,
  actions: {
    set_percent_open: Link<
      {| percent_open: PercentInteger |},
      BackstageOrDeviceResponse
    >,
  },
};
/*

### xr724_wifi_configuration
* This feature has never been synthesized client-side.
* This feature has only ever been sent down from the server.
* This feature indicates that the device is an XR724 and should show a
  clientside wizard allowing the user to configure the device's WiFi connection.

*/
export type XR724WifiConfigurationFeature = {
  name: "xr724_wifi_configuration",
  // This "get_last_update_time" Link may be used by the wizard to discover
  // when the XR724 has reconnected (using the new WiFi configuration). If the
  // device was last updated at a time after when the wizard knew it was
  // offline, then the device must have reconnected.
  get_last_update_time: Link<
    {},
    ServerResponse<{| last_update_time: ?DateString |}>
  >,
  // We don't use these wizard images anymore; instead we use the mp4s found in
  // src/cdn.
  wizard_images: XR724WizardImages,
};

// The mobile client should ignore features it doesn't know about.
type UnknownFeature = {
  name: string,
};

export type Feature =
  | AccessCodeFeature
  | ActionButtonsFeature
  | ActivateLockFeature
  | AdvancedInfoFeature
  | AlarmFeature
  | BarrierFeature
  | BatteryFeature
  | ButtonPadFeature
  | CameraFeature
  | ColoredFeature
  | ConnectionFeature
  | DimmerFeature
  | DragonflyWifiConfigurationFeature
  | FanFeature
  | GroupFeature
  | LockLockFeature
  | OccupancyFeature
  | PanTiltFeature
  | PivotFeature
  | RoomIqFeature
  | RuntimeHistoryFeature
  | ScheduleFeature
  | SensorFeature
  | SolarPanelFeature
  | SpeakerFeature
  | SprinklerFeature
  | StreamableFeature
  | SwitchFeature
  | ThermostatFeature
  | UnenrollFeature
  | UnknownFeature
  | UnlockLockFeature
  | ValveFeature
  | WindowCoveringFeature
  | WindowShadeOpenCloseStopFeature
  | WindowShadeSetPercentOpenFeature
  | XR724WifiConfigurationFeature;



// WEBPACK FOOTER //
// ./src/models/mixins/device_features/types.js